import { useContext, useEffect, useState } from "react";
import { useLocation } from "wouter";
import { styled, darkTheme } from "~/ui/style/stitches.config";
import { useSnapshot } from "valtio";
import { localState } from "~/state/state";
import { AuthContext } from "~/utils/useAuth";
import { Button } from "~/ui/components/Button";
import { SimpleLoadingIndicator } from "~/pages/loading/LoadingPage";
import {
  getTodaysSpread,
  getAllDailySpreads,
  DailySpread,
} from "~/api/dailySpreadApi";

// Test mode: check URL for ?test
const isTestMode = () => window.location.search.includes("test");

// Predetermined background colors that cycle daily (matches DailySpreadPage)
const BACKGROUND_COLORS = [
  "#daeea1", // light green
  "#ecd8ce", // tan
  "#e9b8ff", // light purple
  "#edc29a", // orange
  "#e6e6e6", // light gray
];

// Get deterministic background color based on date (or random in test mode)
const getBackgroundColor = (): string => {
  if (isTestMode()) {
    return BACKGROUND_COLORS[Math.floor(Math.random() * BACKGROUND_COLORS.length)];
  }
  const today = new Date();
  const dayOfYear = Math.floor(
    (today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) /
      (1000 * 60 * 60 * 24)
  );
  return BACKGROUND_COLORS[dayOfYear % BACKGROUND_COLORS.length];
};

export const DailySpreadWidget = ({ shouldShow }: { shouldShow: boolean }) => {
  const { user } = useContext(AuthContext);
  const [, setLocation] = useLocation();
  const [spread, setSpread] = useState<DailySpread | null>(null);
  const [loading, setLoading] = useState(true);
  const [dailyColor] = useState(() => getBackgroundColor());
  const { appDarkMode } = useSnapshot(localState);
  // Dark mode: drop the daily color and use the dark surface
  const backgroundColor = appDarkMode ? "var(--colors-appBg)" : dailyColor;

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch today's spread (or random in test mode)
        let selectedSpread: DailySpread;
        if (isTestMode()) {
          const allSpreads = await getAllDailySpreads();
          selectedSpread = allSpreads[Math.floor(Math.random() * allSpreads.length)];
        } else {
          selectedSpread = await getTodaysSpread();
        }
        setSpread(selectedSpread);
      } catch (error) {
        console.error("Failed to fetch daily spread:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleClick = () => {
    if (!user) {
      setLocation("/signup?dest=/daily-spread");
    } else {
      setLocation("/daily-spread");
    }
  };

  if (!shouldShow) {
    return null;
  }

  if (loading || !spread) {
    return (
      <SectionContainer>
        <LoadingWrapper>
          <SimpleLoadingIndicator />
        </LoadingWrapper>
      </SectionContainer>
    );
  }

  const numPositions = spread.positions.length;
  const cardWidth = numPositions >= 3 ? 60 : 90;
  const cardHeight = Math.round(cardWidth * 1.617);

  return (
    <SectionContainer onClick={() => {
      handleClick();
    }}>
      <WidgetContent>
        <Title>Spread of the day.</Title>
        <Subtitle>A fresh one every morning. On us!</Subtitle>

        <SpreadBox style={{ padding: numPositions === 1 ? "24px 68px" : "24px 54px", backgroundColor }}>
          <TitlePill>{spread.title}</TitlePill>
          <CardsGrid style={{ gridTemplateColumns: `repeat(${numPositions}, ${cardWidth}px)` }}>
            {spread.positions.map((position, index) => (
              <PositionLabel key={`label-${index}`}>{position}:</PositionLabel>
            ))}
            {spread.positions.map((_, index) => (
              <EmptyCard key={`card-${index}`} style={{ width: cardWidth, height: cardHeight }} />
            ))}
          </CardsGrid>
        </SpreadBox>

        <Button
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
          style={{
            fontSize: "12px",
            padding: "12px 0px",
            width: "150px",
            borderRadius: "30px",
            marginTop: "16px",
          }}
          darkHover
        >
          Pull your cards
        </Button>
      </WidgetContent>
    </SectionContainer>
  );
};

const SectionContainer = styled("div", {
  marginTop: "20px",
  marginBottom: "4px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "600px",
  border: "0.5px solid rgba(0, 0, 0, 0.4)",
  borderRadius: "31px",
  boxShadow: "0px 3px 0px 0px rgba(0, 0, 0, 1)",
  padding: "50px 0px",
  backgroundColor: "rgb(235, 235, 235)",
  "@mobile": {
    width: "calc(100% - 32px)",
    marginLeft: "16px",
    marginRight: "16px",
  },
  [`.${darkTheme.className} &`]: { backgroundColor: "$surface", color: "$text", borderColor: "rgba(255, 255, 255, 0.4)", boxShadow: "0px 3px 0px 0px rgba(255, 255, 255, 0.6)", "& button": { backgroundColor: "$inverseSurface", color: "$inverseText" } },
});

const WidgetContent = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const LoadingWrapper = styled("div", {
  position: "relative",
  height: "100px",
  width: "100%",
});

const Title = styled("div", {
  fontFamily: "KeplerLightCondensed",
  fontSize: "24px",
  textAlign: "center",
  "@mobile": {
    fontSize: "26px",
  },
});

const Subtitle = styled("div", {
  fontSize: "12px",
  lineHeight: "1.4em",
  marginTop: "8px",
  marginBottom: "20px",
  textAlign: "center",
  maxWidth: "380px",
  padding: "0px 20px",
});

const TitlePill = styled("div", {
  backgroundColor: "#1a1a1a",
  color: "#fff",
  padding: "7px 20px",
  borderRadius: "30px",
  fontSize: "11px",
  fontWeight: "400",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginBottom: "18px",
  [`.${darkTheme.className} &`]: { backgroundColor: "$inverseSurface", color: "$inverseText" },
});

const SpreadBox = styled("div", {
  cursor: "pointer",
  backgroundColor: "#f6f6f6",
  borderRadius: "20px",
  padding: "24px 20px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  border: "1px solid #000",
  position: "relative",
  zIndex: 1,
  transition: "transform 0.15s ease, scale 0.15s ease, opacity 0.15s ease",
  "&:hover": {
    transform: "translateY(3px)",
    opacity: 0.9,
    scale: ".99",
  },
  [`.${darkTheme.className} &`]: { borderColor: "$border" },
});

const CardsGrid = styled("div", {
  display: "grid",
  gap: "8px 16px",
  justifyItems: "center",
  alignItems: "end",
});

const PositionLabel = styled("div", {
  fontSize: "12px",
  fontWeight: "500",
  lineHeight: "1.4em",
  textAlign: "center",
});

const EmptyCard = styled("div", {
  borderRadius: "7px",
  border: "1px dashed rgba(0, 0, 0, 1)",
  backgroundColor: "#fff",
  [`.${darkTheme.className} &`]: { borderColor: "$textMuted", backgroundColor: "$surface" },
});
